import * as React from "react"
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router"
import { Moon, Sun } from "lucide-react"

import { useAccessToken, getRequestedLocation, clearRequestedLocation } from "@/lib/auth"
import { useTheme } from "@/components/theme-provider"
import { PageSkeleton } from "@/components/console/Fallbacks"
import { SignIn } from "@/components/console/SignIn"
import { Home } from "@/pages/Home"
import { Search } from "@/pages/Search"
import { Health } from "@/pages/Health"
import { Audience } from "@/pages/Audience"
import { Maintenance } from "@/pages/Maintenance"
import { Record } from "@/pages/Record"
import { RecordEdit } from "@/pages/RecordEdit"
import { History, HistoryVersion } from "@/pages/History"

const NAV = [
  { path: "/", label: "home" },
  { path: "/search", label: "search" },
  { path: "/health", label: "health" },
]

function Shell({ children }: { children: React.ReactNode }) {
  const { theme, setTheme } = useTheme()
  const location = useLocation()
  const resolvedDark =
    theme === "dark" ||
    (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches)

  return (
    <div className="flex h-svh bg-background text-foreground">
      <aside className="flex w-44 shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground">
        <div className="flex items-center gap-2 border-b border-sidebar-border px-3 py-3">
          <img aria-hidden alt="" src="/icon.png" className="size-4 shrink-0" />
          <span className="font-mono text-xs font-medium tracking-tight">brain console</span>
        </div>
        <nav className="flex-1 space-y-0.5 p-2" aria-label="Console pages">
          {NAV.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              aria-current={location.pathname === item.path ? "page" : undefined}
              className={`flex w-full items-center justify-between px-2 py-1.5 text-left font-mono text-xs focus-visible:outline-2 focus-visible:outline-ring ${
                location.pathname === item.path
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "text-muted-foreground hover:bg-sidebar-accent/60"
              }`}
            >
              <span>
                {location.pathname === item.path ? "▸ " : "  "}
                {item.label}
              </span>
            </Link>
          ))}
        </nav>
        <div className="border-t border-sidebar-border p-2">
          <button
            onClick={() => setTheme(resolvedDark ? "light" : "dark")}
            title="Toggle theme (or press d)"
            className="flex w-full items-center gap-2 px-2 py-1.5 font-mono text-xs text-muted-foreground hover:bg-sidebar-accent/60 focus-visible:outline-2 focus-visible:outline-ring"
          >
            {resolvedDark ? <Sun className="size-3.5" /> : <Moon className="size-3.5" />}
            {resolvedDark ? "light" : "dark"}
          </button>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <main className="min-h-0 flex-1 overflow-y-auto p-4">{children}</main>
      </div>
    </div>
  )
}

function AppRoutes({ token }: { token: string }) {
  const navigate = useNavigate()

  React.useEffect(() => {
    const requestedLocation = getRequestedLocation()
    if (requestedLocation && requestedLocation !== "/") {
      navigate(requestedLocation)
      clearRequestedLocation()
    }
  }, [navigate])

  return (
    <Routes>
      <Route path="/" element={<Home token={token} />} />
      <Route path="/search" element={<Search token={token} />} />
      <Route path="/audiences/:audience" element={<Audience token={token} />} />
      <Route
        path="/audiences/:audience/maintenance"
        element={<Maintenance token={token} />}
      />
      <Route path="/records/new" element={<RecordEdit token={token} />} />
      <Route path="/records/:audience/:id" element={<Record token={token} />} />
      <Route path="/records/:audience/:id/edit" element={<RecordEdit token={token} />} />
      <Route path="/records/:audience/:id/history" element={<History token={token} />} />
      <Route
        path="/records/:audience/:id/history/:version"
        element={<HistoryVersion token={token} />}
      />
      <Route path="/health" element={<Health token={token} />} />
    </Routes>
  )
}

export function App() {
  const { token, isReady, signIn } = useAccessToken()

  // Not settled yet: MSAL is still processing a redirect response or acquiring
  // silently. A skeleton is right here -- this really is loading.
  if (!isReady) {
    return (
      <div className="flex h-svh items-center justify-center bg-background p-4">
        <PageSkeleton />
      </div>
    )
  }

  // Settled, and nobody is signed in. A skeleton would be a lie: nothing is
  // loading and nothing will, until someone signs in.
  if (!token) {
    return <SignIn onSignIn={signIn} />
  }

  return (
    <BrowserRouter>
      <Shell>
        <AppRoutes token={token} />
      </Shell>
    </BrowserRouter>
  )
}

export default App
